/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  PaginationState,
  OnChangeFn,
} from '@tanstack/react-table';
import { cn } from '@/utils';
import { LoadingSpinner } from './LoadingSpinner';
import { FilterBar } from './FilterBar';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  Info,
  MoreVertical,
  LucideIcon,
} from 'lucide-react';

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: TData) => void;
  onRowDoubleClick?: (item: TData) => void;
  onRowRightClick?: (event: React.MouseEvent, item: TData) => void;
  className?: string;
  pageSize?: number;
  showSearch?: boolean;
  searchPlaceholder?: string;
  // Mobile card render
  mobileCardRender?: (item: TData, index: number) => React.ReactNode;
  // Row selection with checkbox
  selectedRowId?: string | null;
  getRowId?: (item: TData) => string;
  // Action icon column (Book, Cancel, Invoice, etc.)
  actionIcon?: LucideIcon;
  actionIconColor?: string;
  actionIconTitle?: string;
  onActionClick?: (item: TData) => void;
  // Properties icon column
  onPropertiesClick?: (item: TData) => void;
  // Three-dot menu button
  onMenuClick?: (event: React.MouseEvent, item: TData) => void;
  // Per-row predicate to hide the three-dot menu button (e.g. rows with no menu items)
  menuVisible?: (item: TData) => boolean;
  // Extra action buttons next to the three-dot menu
  // `title` and `color` may be per-row functions when the icon reflects that row's state
  // (e.g. the storefront toggle: green + "shown" vs grey + "not shown").
  extraRowActions?: Array<{
    icon: LucideIcon;
    title: string | ((item: TData) => string);
    onClick: (item: TData) => void;
    color?: string | ((item: TData) => string);
    visible?: (item: TData) => boolean;
    disabled?: (item: TData) => boolean;
  }>;
  /**
   * Renders what a row is made of, under the row, behind a chevron in a leading column. Opt-in: without
   * it the table has no expander column and behaves exactly as before.
   *
   * Return null for a row with nothing to show and it stays un-expandable, rather than offering a
   * chevron that opens an empty panel.
   */
  renderExpandedRow?: (item: TData) => React.ReactNode;
  // Per-row className override (e.g. highlight problematic rows)
  getRowClassName?: (item: TData) => string | undefined;
  // Per-row tooltip (HTML title attribute), e.g. explanation of the highlight
  getRowTitle?: (item: TData) => string | undefined;
  // Server-side (manual) pagination. When manualPagination is true the PARENT owns paging:
  // `data` is already the current page, `pageCount`/`totalCount` come from the server, and page/size
  // changes are reported via onPageChange/onPageSizeChange (0-based pageIndex). When false/absent the
  // table paginates the full `data` array client-side exactly as before.
  manualPagination?: boolean;
  pageCount?: number;
  totalCount?: number;
  pageIndex?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  onRowClick,
  onRowDoubleClick,
  onRowRightClick,
  className,
  pageSize = 10,
  showSearch = true,
  searchPlaceholder = 'Search...',
  mobileCardRender,
  selectedRowId,
  getRowId,
  actionIcon: ActionIcon,
  actionIconColor = 'text-blue-500 hover:text-blue-700',
  actionIconTitle = 'Action',
  onActionClick,
  onPropertiesClick,
  onMenuClick,
  menuVisible,
  extraRowActions,
  renderExpandedRow,
  getRowClassName,
  getRowTitle,
  manualPagination = false,
  pageCount,
  totalCount,
  pageIndex,
  onPageChange,
  onPageSizeChange,
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  // Which rows are open. Keyed by the table's row id so the set survives sorting and paging.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  // In manual mode the pagination state is controlled by the parent; otherwise the table owns it.
  const paginationState: PaginationState = manualPagination
    ? { pageIndex: pageIndex ?? 0, pageSize }
    : internalPagination;
  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === 'function' ? updater(paginationState) : updater;
    if (manualPagination) {
      if (next.pageIndex !== paginationState.pageIndex) onPageChange?.(next.pageIndex);
      if (next.pageSize !== paginationState.pageSize) onPageSizeChange?.(next.pageSize);
    } else {
      setInternalPagination(next);
    }
  };

  // Use refs for callbacks so cell renderers always call the latest version
  // without triggering column re-creation on every render
  const onRowClickRef = useRef(onRowClick);
  const onActionClickRef = useRef(onActionClick);
  const onPropertiesClickRef = useRef(onPropertiesClick);
  const onMenuClickRef = useRef(onMenuClick);
  const menuVisibleRef = useRef(menuVisible);
  const extraRowActionsRef = useRef(extraRowActions);
  onRowClickRef.current = onRowClick;
  onActionClickRef.current = onActionClick;
  onPropertiesClickRef.current = onPropertiesClick;
  onMenuClickRef.current = onMenuClick;
  menuVisibleRef.current = menuVisible;
  extraRowActionsRef.current = extraRowActions;

  // Build columns with checkbox, action icons, and properties icon if needed
  const enhancedColumns = React.useMemo(() => {
    const cols: ColumnDef<TData, unknown>[] = [];

    // Add three-dot menu column (and extra action buttons) if onMenuClick is provided
    if (onMenuClick || extraRowActions?.length) {
      const hasMenu = !!onMenuClick;
      const actionsCount = (hasMenu ? 1 : 0) + (extraRowActions?.length || 0);
      cols.push({
        id: '_menu',
        header: () => null,
        cell: ({ row }) => (
          <div className="flex items-center justify-start gap-0.5">
            {hasMenu && (!menuVisibleRef.current || menuVisibleRef.current(row.original)) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMenuClickRef.current?.(e, row.original);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title={t('dataTable.actions', 'Actions')}
              >
                <MoreVertical className="h-4 w-4 text-gray-500 hover:text-gray-700" />
              </button>
            )}
            {extraRowActionsRef.current?.map((action, idx) => {
              if (action.visible && !action.visible(row.original)) return null;
              const Icon = action.icon;
              const isDisabled = action.disabled ? action.disabled(row.original) : false;
              const actionTitle = typeof action.title === 'function' ? action.title(row.original) : action.title;
              const actionColor = typeof action.color === 'function' ? action.color(row.original) : action.color;
              return (
                <button
                  key={idx}
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    action.onClick(row.original);
                  }}
                  className={`p-1 rounded transition-colors ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : `hover:bg-gray-100 ${actionColor || 'text-blue-500 hover:text-blue-700'}`
                  }`}
                  title={actionTitle}
                >
                  <Icon className={`h-4 w-4 ${isDisabled ? 'animate-spin' : ''}`} />
                </button>
              );
            })}
          </div>
        ),
        size: 40 * actionsCount,
      });
    }

    // Add checkbox column if selection is enabled
    if (getRowId && selectedRowId !== undefined) {
      cols.push({
        id: '_select',
        header: () => null,
        cell: ({ row }) => {
          const rowId = getRowId(row.original);
          const isSelected = rowId === selectedRowId;
          return (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onRowClickRef.current?.(row.original)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
            </div>
          );
        },
        size: 40,
      });
    }

    // Add actions column (action icon + properties icon) if either is enabled
    if (ActionIcon || onPropertiesClick) {
      cols.push({
        id: '_actions',
        header: () => null,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {ActionIcon && onActionClickRef.current && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClickRef.current?.(row.original);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title={actionIconTitle}
              >
                <ActionIcon className={`h-4 w-4 ${actionIconColor}`} />
              </button>
            )}
            {onPropertiesClickRef.current && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPropertiesClickRef.current?.(row.original);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title={t('dataTable.viewProperties')}
              >
                <Info className="h-4 w-4 text-gray-500 hover:text-indigo-600" />
              </button>
            )}
          </div>
        ),
        size: ActionIcon && onPropertiesClick ? 70 : 40,
      });
    }

    // Add original columns
    cols.push(...columns);

    return cols;
  }, [columns, getRowId, selectedRowId, ActionIcon, actionIconColor, actionIconTitle, onPropertiesClick, onMenuClick, extraRowActions, t]);

  const table = useReactTable({
    data,
    columns: enhancedColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination: paginationState,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: handlePaginationChange,
    manualPagination: manualPagination || undefined,
    pageCount: manualPagination ? (pageCount ?? -1) : undefined,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Don't reset pagination/sorting when data reference changes (e.g. async refresh)
    autoResetPageIndex: false,
    autoResetExpanded: false,
  });

  // autoResetPageIndex is off, so clamp the current page whenever the page count shrinks —
  // i.e. the user changes the rows-per-page selector or the data set gets smaller. Without this
  // the table can sit on a page past the last one and render an empty list ("Showing 51 to 10").
  // Skipped in manual mode, where the parent owns the page index.
  const tablePageCount = table.getPageCount();
  const currentPageIndex = table.getState().pagination.pageIndex;
  useEffect(() => {
    if (!manualPagination && tablePageCount > 0 && currentPageIndex > tablePageCount - 1) {
      table.setPageIndex(tablePageCount - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablePageCount, currentPageIndex, manualPagination]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner text="Loading data..." />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search — the same bar the filter-driven pages use, so every list looks alike */}
      {showSearch && (
        <FilterBar
          searchQuery={globalFilter ?? ''}
          onSearchChange={setGlobalFilter}
          onClearSearch={() => setGlobalFilter('')}
          searchPlaceholder={searchPlaceholder}
          card={false}
        />
      )}

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {renderExpandedRow && <th className="w-8 px-2 py-3" />}
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                      header.column.getCanSort() && 'cursor-pointer select-none hover:bg-gray-100'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-2">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="text-gray-400">
                          {{
                            asc: <ChevronUp className="h-4 w-4" />,
                            desc: <ChevronDown className="h-4 w-4" />,
                          }[header.column.getIsSorted() as string] ?? (
                            <div className="h-4 w-4 opacity-0 group-hover:opacity-50">
                              <ChevronUp className="h-2 w-2" />
                              <ChevronDown className="h-2 w-2" />
                            </div>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => {
              const isSelected = getRowId && selectedRowId !== undefined && getRowId(row.original) === selectedRowId;
              const customRowClass = getRowClassName?.(row.original);
              const customRowTitle = getRowTitle?.(row.original);
              // Null = this row has nothing behind it, so it gets no chevron rather than an empty panel.
              const expandedContent = renderExpandedRow?.(row.original) ?? null;
              const isExpanded = expandedRows.has(row.id);
              const expander = renderExpandedRow ? (
                <td className="w-8 px-2 py-2 align-middle">
                  {expandedContent && (
                    <button
                      type="button"
                      className="p-0.5 rounded hover:bg-gray-200 text-gray-500"
                      aria-expanded={isExpanded}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedRows((prev) => {
                          const next = new Set(prev);
                          if (next.has(row.id)) next.delete(row.id);
                          else next.add(row.id);
                          return next;
                        });
                      }}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )}
                </td>
              ) : null;
              const mainRow = (
                <tr
                  key={row.id}
                  className={cn(
                    'transition-colors',
                    (onRowClick || onRowDoubleClick) && 'cursor-pointer hover:bg-gray-50 active:bg-gray-100',
                    isSelected && 'bg-indigo-50 hover:bg-indigo-100',
                    customRowClass
                  )}
                  title={customRowTitle}
                  onClick={() => onRowClick?.(row.original)}
                  onDoubleClick={() => onRowDoubleClick?.(row.original)}
                  onContextMenu={(e) => onRowRightClick?.(e, row.original)}
                >
                  {expander}
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 lg:px-6 py-2 whitespace-nowrap text-sm text-gray-900"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
              return expandedContent && isExpanded
                ? [
                    mainRow,
                    <tr key={`${row.id}-detail`} className="bg-gray-50">
                      <td colSpan={row.getVisibleCells().length + 1} className="px-4 lg:px-6 py-3">
                        {expandedContent}
                      </td>
                    </tr>,
                  ]
                : mainRow;
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        {table.getRowModel().rows.map((row, index) => {
          const isSelected = getRowId && selectedRowId !== undefined && getRowId(row.original) === selectedRowId;
          const customRowClass = getRowClassName?.(row.original);
          const customRowTitle = getRowTitle?.(row.original);

          if (mobileCardRender) {
            return (
              <div
                key={row.id}
                className={cn(
                  'p-2 bg-white transition-colors',
                  (onRowClick || onRowDoubleClick) && 'cursor-pointer active:bg-gray-50',
                  isSelected && 'bg-indigo-50',
                  customRowClass
                )}
                title={customRowTitle}
                onClick={() => onRowClick?.(row.original)}
                onDoubleClick={() => onRowDoubleClick?.(row.original)}
                onContextMenu={(e) => onRowRightClick?.(e, row.original)}
              >
                <div className="flex items-start gap-1">
                  {/* Three-dot menu for mobile */}
                  {onMenuClick && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onMenuClick(e, row.original);
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0 mt-1"
                      title={t('dataTable.actions', 'Actions')}
                    >
                      <MoreVertical className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    </button>
                  )}
                  {/* Checkbox for mobile */}
                  {getRowId && selectedRowId !== undefined && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onRowClick?.(row.original)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                    />
                  )}
                  {/* Action icons for mobile */}
                  {(ActionIcon || onPropertiesClick) && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {ActionIcon && onActionClick && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onActionClick(row.original);
                          }}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={actionIconTitle}
                        >
                          <ActionIcon className={`h-4 w-4 ${actionIconColor}`} />
                        </button>
                      )}
                      {onPropertiesClick && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPropertiesClick(row.original);
                          }}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={t('dataTable.viewProperties')}
                        >
                          <Info className="h-4 w-4 text-gray-500 hover:text-indigo-600" />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {mobileCardRender(row.original, index)}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={row.id}
              className={cn(
                'p-4 bg-white space-y-2 transition-colors',
                (onRowClick || onRowDoubleClick) && 'cursor-pointer active:bg-gray-50',
                isSelected && 'bg-indigo-50',
                customRowClass
              )}
              title={customRowTitle}
              onClick={() => onRowClick?.(row.original)}
              onDoubleClick={() => onRowDoubleClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <div key={cell.id} className="flex justify-between items-start gap-2">
                  <span className="text-xs font-medium text-gray-500 uppercase shrink-0">
                    {typeof cell.column.columnDef.header === 'string'
                      ? cell.column.columnDef.header
                      : cell.column.id}
                  </span>
                  {/* min-w-0 + break-words so long values (e.g. emails) wrap instead of overflowing */}
                  <span className="text-sm text-gray-900 text-right break-words min-w-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
        <div className="text-sm text-gray-500">
          {(() => {
            const resultCount = manualPagination
              ? (totalCount ?? data.length)
              : table.getFilteredRowModel().rows.length;
            const pageIdx = table.getState().pagination.pageIndex;
            const size = table.getState().pagination.pageSize;
            const from = resultCount === 0 ? 0 : pageIdx * size + 1;
            const to = Math.min((pageIdx + 1) * size, resultCount);
            return <>{t('dataTable.showing', 'Showing {{from}} to {{to}} of {{total}} results',
              { from, to, total: resultCount })}</>;
          })()}
        </div>
        <div className="flex items-center gap-2">
          {/* Page Size Selector */}
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {[5, 10, 20, 25, 30, 50, 100].map((size) => (
              <option key={size} value={size}>
                {t('dataTable.perPage', { size })}
              </option>
            ))}
          </select>

          {/* Pagination Buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className={cn(
                'p-2 rounded-md transition-colors',
                table.getCanPreviousPage()
                  ? 'hover:bg-gray-100 text-gray-700'
                  : 'text-gray-300 cursor-not-allowed'
              )}
              title={t('dataTable.firstPage')}
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className={cn(
                'p-2 rounded-md transition-colors',
                table.getCanPreviousPage()
                  ? 'hover:bg-gray-100 text-gray-700'
                  : 'text-gray-300 cursor-not-allowed'
              )}
              title={t('dataTable.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {generatePageNumbers(
                table.getState().pagination.pageIndex,
                table.getPageCount()
              ).map((pageNum, idx) => (
                <React.Fragment key={idx}>
                  {pageNum === '...' ? (
                    <span className="px-2 py-1 text-gray-400">...</span>
                  ) : (
                    <button
                      onClick={() => table.setPageIndex(pageNum as number)}
                      className={cn(
                        'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                        table.getState().pagination.pageIndex === pageNum
                          ? 'bg-indigo-500 text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      )}
                    >
                      {(pageNum as number) + 1}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>

            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className={cn(
                'p-2 rounded-md transition-colors',
                table.getCanNextPage()
                  ? 'hover:bg-gray-100 text-gray-700'
                  : 'text-gray-300 cursor-not-allowed'
              )}
              title={t('dataTable.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className={cn(
                'p-2 rounded-md transition-colors',
                table.getCanNextPage()
                  ? 'hover:bg-gray-100 text-gray-700'
                  : 'text-gray-300 cursor-not-allowed'
              )}
              title={t('dataTable.lastPage')}
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to generate page numbers with ellipsis
function generatePageNumbers(currentPage: number, totalPages: number): (number | '...')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const pages: (number | '...')[] = [];

  // Always show first page
  pages.push(0);

  if (currentPage > 3) {
    pages.push('...');
  }

  // Show pages around current page
  const start = Math.max(1, currentPage - 1);
  const end = Math.min(totalPages - 2, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 4) {
    pages.push('...');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages - 1);
  }

  return pages;
}

// Re-export Column type for convenience
export type { ColumnDef };
